/**
 * One warehouse's record, the list it can restock from, and the editable copy
 * of its own fields.
 *
 * Reading only. The writes are `useWarehouseMutations`, which needs the setters
 * this returns to reflect what it saved.
 */
import { useState, useEffect } from 'react';

import {
  fetchWarehouseById,
  fetchWarehouses,
  type WarehouseWithRelations,
  type Warehouse,
} from '../../api/warehouse';

export interface WarehouseFormData {
  name: string;
  code: string;
  description: string;
}

export function useWarehouseRecord(id: string | undefined) {
  const [warehouse, setWarehouse] = useState<WarehouseWithRelations | null>(null);
  const [allWarehouses, setAllWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<WarehouseFormData>({
    name: '',
    code: '',
    description: '',
  });
  const [selectedRestockSourceId, setSelectedRestockSourceId] = useState<string | null>(null);

  useEffect(() => {
    void loadData();
  }, [id]);

  async function loadData() {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [whData, warehousesData] = await Promise.all([
        fetchWarehouseById(id),
        fetchWarehouses(false),
      ]);
      setWarehouse(whData);
      setAllWarehouses(warehousesData);
      setSelectedRestockSourceId(whData.sourceWarehouseId || null);
      setFormData({
        name: whData.name,
        code: whData.code || '',
        description: whData.description || '',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load warehouse');
    } finally {
      setLoading(false);
    }
  }

  return {
    warehouse,
    setWarehouse,
    // A warehouse cannot restock from itself, so it is not among its own options.
    restockOptions: allWarehouses.filter((w) => w.id !== warehouse?.id),
    loading,
    error,
    setError,
    formData,
    setFormData,
    selectedRestockSourceId,
    setSelectedRestockSourceId,
    loadData,
  };
}
